import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  ADAPTATIONS,
  CALL_ENDS,
  FiveJobsTableComponent,
  GENERAL_TROUBLESHOOTING,
  IMPROVEMENTS_HEADING,
  IMPROVEMENTS_INTRO,
  LESSON_JOBS,
  LESSON_RULE,
  OTHER_SETUPS,
  RECOMMENDED_SETUP,
  RECOMMENDED_SHOPPING_LIST,
  SETUP_STEPS,
  SignalFlowDiagramComponent,
  WHAT_I_USE,
  jobLabel,
  shoppingTotal,
  type LessonJobId,
  type SetupProfile,
  type SetupStep,
} from '@site/zoom-setup';
import { CalloutComponent } from '../../../../content/callout/callout.component';
import { ContentSectionComponent } from '../../../../content/content-section/content-section.component';
import { OpenNewTab } from '../../../../content/directives/open-new-tab.directive';
import { DisclosureComponent } from '../../../../content/disclosure/disclosure.component';
import {
  JumpNavComponent,
  type JumpNavLink,
} from '../../../../content/jump-nav/jump-nav.component';
import { LightboxImageComponent } from '../../../../content/lightbox-image/lightbox-image.component';
import { ResourceRowComponent } from '../../../../content/resource-row/resource-row.component';

interface PracticeApp {
  readonly name: string;
  readonly href?: string;
  readonly description: string;
}

const PRACTICE_APPS: readonly PracticeApp[] = [
  {
    name: 'The Amazing Slow Downer',
    href: 'https://www.ronimusic.com',
    description:
      'Slows down audio without changing the pitch. Essential for learning songs and transcribing bass lines. Play along at half speed and gradually increase the tempo as you get comfortable. Available for Windows, Mac, iOS, and Android.',
  },
  {
    name: 'iReal Pro',
    href: 'https://irealpro.com',
    description:
      'Generates realistic backing tracks from chord charts. Great for jazz study, practicing chord changes, and playing along with a virtual band. Available for iOS, Mac, and Android. A Windows version is currently in beta.',
  },
  {
    name: 'Metronome',
    description:
      'A good metronome is essential for developing solid time. Look for one that supports subdivision settings: whole, half, quarter, eighth, triplet, 16th, and swing eighths. Most app stores have several free and paid options.',
  },
];

@Component({
  selector: 'app-zoom-setup-content',
  imports: [
    CalloutComponent,
    ContentSectionComponent,
    DisclosureComponent,
    FiveJobsTableComponent,
    JumpNavComponent,
    LightboxImageComponent,
    NgOptimizedImage,
    OpenNewTab,
    RouterLink,
    ResourceRowComponent,
    SignalFlowDiagramComponent,
  ],
  templateUrl: './zoom-setup-content.component.html',
  styleUrl: './zoom-setup-content.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ZoomSetupContentComponent {
  protected readonly practiceApps = PRACTICE_APPS;

  protected readonly lessonJobs = LESSON_JOBS;
  protected readonly lessonRule = LESSON_RULE;
  protected readonly callEnds = CALL_ENDS;
  protected readonly whatIUse = WHAT_I_USE;
  protected readonly improvementsHeading = IMPROVEMENTS_HEADING;
  protected readonly improvementsIntro = IMPROVEMENTS_INTRO;
  protected readonly recommended = RECOMMENDED_SETUP;
  protected readonly adaptations = ADAPTATIONS;
  protected readonly otherSetups = OTHER_SETUPS;
  protected readonly generalTroubleshooting = GENERAL_TROUBLESHOOTING;
  protected readonly jobLabel = jobLabel;
  protected readonly shopping = RECOMMENDED_SHOPPING_LIST;
  protected readonly shoppingTotal = shoppingTotal(RECOMMENDED_SHOPPING_LIST);

  protected readonly jumpNavLinks: readonly JumpNavLink[] = [
    { label: 'How Lesson Audio Works', fragment: 'five-jobs' },
    { label: 'Shopping List', fragment: 'shopping-list' },
    { label: 'Cable Reference', fragment: 'gear-reference' },
    { label: 'Setup Steps', fragment: 'setup-steps' },
    { label: 'Computer Settings', fragment: 'computer-settings' },
    { label: 'Zoom Settings', fragment: 'zoom-settings' },
    { label: 'Mixer Levels', fragment: 'mixer-levels' },
    { label: 'Improve the Lesson', fragment: 'improve' },
    { label: 'Other Setups', fragment: 'other-setups' },
    { label: 'Troubleshooting', fragment: 'troubleshooting' },
    { label: 'Tips', fragment: 'tips' },
    { label: 'Apps for Lessons', fragment: 'apps-for-lessons' },
  ];

  private step(stepId: string): SetupStep {
    const step = SETUP_STEPS.find((s) => s.id === stepId);
    if (step === undefined) {
      throw new Error(`unknown setup step: ${stepId}`);
    }
    return step;
  }

  /** "Job: X" or "Jobs: X, Y" for a step id; the template reads it per step. */
  protected stepJobs(stepId: string): string {
    const labels = this.step(stepId).jobs.map((j: LessonJobId) => jobLabel(j));
    return `${labels.length === 1 ? 'Job' : 'Jobs'}: ${labels.join(', ')}`;
  }

  /** The step's title, so the template never carries its own copy. */
  protected stepTitle(stepId: string): string {
    return this.step(stepId).title;
  }

  /** "The five jobs with X" for a worked-example setup's table caption. */
  protected captionFor(profile: SetupProfile): string {
    return `The five jobs with ${profile.name.charAt(0).toLowerCase()}${profile.name.slice(1)}`;
  }
}
